import { useParams } from 'react-router-dom';
import {
  ButtonPrimary,
  ButtonSecondary,
  Popup,
  SidePanel,
  SidePanelType,
} from '@ui/src';
import { useAppSelector } from '@store';
import { OrganizationType } from '../types';
import { GeneralInfo } from '../../individual/types';
import { CorporateGeneralInfo } from '../../corporate/types';
import { LinkCustomerFormTypes } from './types';
import useLinkedCustomerForm from './hooks/use-linked-clients-form';
import LinkedClientsForm from '../../../components/common/linked-clients/form';

const LinkedCustomerForm = ({
  type,
  setExternalPanelIsOpen,
  selectedPartner,
  linkExternalCustomerPopupVisible,
  setLinkExternalCustomerPopupVisible,
  parent,
  selectedPspIntegrationId: selectedPspPartnerIntegrationId,
}: LinkCustomerFormTypes) => {
  const { pspIntegrations, pspPartners } = useAppSelector(
    (state) => state.customersExternalAccounts,
  );
  const { baseInformation: corporateBaseInformation } = useAppSelector(
    (state) => state.corporateCustomers.info.general,
  );
  const { baseInformation: individualBaseInformation } = useAppSelector(
    (state) => state.individualCustomers.info.general,
  );

  const { customerId } = useParams();

  const {
    panelIsOpen,
    isDirty,
    setDirtyPopupVisible,
    handleClose,
    globalError,
    isLoading,
    externalId,
    onBindExternalOrganization,
    control,
    availablePspPartners,
    errors,
    setExternalOrganizationsListVisible,
    externalOrganizationSearch,
    getPspIntegrationOrganizationsAction,
    pspPartnerId,
    pspIntegrationOrganizationsList,
    externalOrganizationsListVisible,
    organizationsIsLoading,
    organizationsIsFetching,
    setExternalOrganizationSearch,
    dirtyPopupVisible,
  } = useLinkedCustomerForm({
    customerId,
    type,
    selectedPspPartnerIntegrationId,
    setExternalPanelIsOpen,
    setLinkExternalCustomerPopupVisible,
    pspPartners,
    pspIntegrations,
    selectedPartner,
    linkExternalCustomerPopupVisible,
  });

  const baseInfo =
    type === OrganizationType.INDIVIDUAL
      ? individualBaseInformation
      : corporateBaseInformation;

  const customerName =
    type === OrganizationType.INDIVIDUAL
      ? `${(baseInfo as Partial<GeneralInfo>)?.firstName} ${(baseInfo as Partial<GeneralInfo>)?.lastName}`
      : (baseInfo as Partial<CorporateGeneralInfo>)?.name;

  return (
    <>
      <SidePanel
        title="Link Client"
        isOpen={panelIsOpen}
        type={SidePanelType.AML}
        parent={parent}
        onClose={() => {
          if (isDirty) {
            setDirtyPopupVisible(true);
          } else {
            handleClose();
          }
        }}
        sidepanelExpandedKey="link-external-organization-panel"
        errorSidepanel={globalError}
        footer={
          <>
            <ButtonPrimary
              text={isLoading ? 'Loading...' : 'Link'}
              disabled={!externalId || isLoading}
              onClick={onBindExternalOrganization}
              bold
            />
            <ButtonSecondary
              text="Close"
              onClick={() => {
                if (isDirty) {
                  setDirtyPopupVisible(true);
                } else {
                  handleClose();
                }
              }}
              className="button-white"
              bold
            />
          </>
        }
        closeOnClickOutside
      >
        <LinkedClientsForm
          control={control}
          availablePspPartners={availablePspPartners}
          selectedPartner={selectedPartner}
          errors={errors}
          customerName={customerName}
          setExternalOrganizationsListVisible={
            setExternalOrganizationsListVisible
          }
          externalOrganizationSearch={externalOrganizationSearch}
          setExternalOrganizationSearch={setExternalOrganizationSearch}
          getPspIntegrationOrganizationsAction={
            getPspIntegrationOrganizationsAction
          }
          pspPartnerId={pspPartnerId}
          pspIntegrationOrganizationsList={pspIntegrationOrganizationsList}
          externalOrganizationsListVisible={externalOrganizationsListVisible}
          organizationsIsLoading={organizationsIsLoading}
          organizationsIsFetching={organizationsIsFetching}
          type={type}
        />
      </SidePanel>
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('external-dirty-popup') as HTMLElement}
      />
      <div id="external-dirty-popup" />
    </>
  );
};

export default LinkedCustomerForm;
